package ru.villawonka.app.presentation.screens.searchFilter.viewmodel

import ru.villawonka.app.common.mvvm.BaseViewModel
import ru.villawonka.app.common.utils.fold
import ru.villawonka.app.domain.model.*
import ru.villawonka.app.domain.service.UserSettingsService
import ru.villawonka.app.domain.usecase.GetUserSettingsUseCase
import ru.villawonka.app.domain.usecase.GetUserCountryUseCase
import ru.villawonka.app.presentation.model.toUIModel
import ru.villawonka.app.presentation.screens.searchFilter.compose.SearchFilterScreen
import ru.villawonka.app.presentation.screens.searchFilter.model.ChipUIModel

class SearchFilterViewModel(
    private val params: SearchFilterScreen.Params.Input,
    private val getUserCountryUseCase: GetUserCountryUseCase,
    private val userSettingsService: UserSettingsService,
    private val getUserSettingsUseCase: GetUserSettingsUseCase,
) : BaseViewModel<SearchFilterUIState>(), UserSettingsService.Listener {

    private var countryInfo: UserCountryDomain? = null
    private var userSettings: UserSettingsDomain? = null
    private var savedSearchParamsDomain: SearchParamsDomain? = null
    private val bedrooms = listOf("1", "2", "3", "4", "5")

    init {
        loadScreen()
        userSettingsService.addDelegate(this)
    }

    override fun createInitialState(): SearchFilterUIState = SearchFilterUIState.Loading

    override fun onDispose() {
        super.onDispose()
        userSettingsService.removeDelegate(this)
    }

    override fun onSettingsChanged() { loadScreen() }

    fun onRetryButtonClick() { loadScreen() }

    fun onClearButtonClick() {
        updateSearchParams(savedSearchParamsDomain?.clear())
    }

    fun onCloseButtonClick() = launch {
        shootEvent(SearchFilterUIEvent.Close(null))
    }

    fun onApplyButtonClick() = launch {
        shootEvent(SearchFilterUIEvent.Close(savedSearchParamsDomain))
    }

    fun onCityValueChanged(newValue: String) = updateSearchParams(
        when (val searchParams = savedSearchParamsDomain) {
            is SearchParamsDomain.RentalItem.Accommodation -> searchParams.copy(
                city = newValue,
                districts = emptyList(),
            )
            is SearchParamsDomain.RentalItem.Transport -> searchParams.copy(
                city = newValue,
                districts = emptyList(),
            )
            else -> null
        }
    )

    fun onDistrictChipClick(chip: ChipUIModel<String>) = updateSearchParams(
        when (val searchParams = savedSearchParamsDomain) {
            is SearchParamsDomain.RentalItem.Accommodation -> searchParams.copy(
                districts = if (chip.selected) {
                    searchParams.districts.minus(chip.payload)
                } else {
                    searchParams.districts.plus(chip.payload)
                },
            )
            else -> null
        }
    )

    fun onCostMinValueChanged(newValue: Int?) = updateSearchParams(
        when (val searchParams = savedSearchParamsDomain) {
            is SearchParamsDomain.RentalItem.Accommodation -> searchParams.copy(costMin = newValue)
            is SearchParamsDomain.RentalItem.Transport -> searchParams.copy(costMin = newValue)
            else -> null
        }
    )

    fun onCostMaxValueChanged(newValue: Int?) = updateSearchParams(
        when (val searchParams = savedSearchParamsDomain) {
            is SearchParamsDomain.RentalItem.Accommodation -> searchParams.copy(costMax = newValue)
            is SearchParamsDomain.RentalItem.Transport -> searchParams.copy(costMax = newValue)
            else -> null
        }
    )

    fun onAccommodationTypeChipClick(chip: ChipUIModel<AccommodationTypeDomain>) = updateSearchParams(
        when (val searchParams = savedSearchParamsDomain) {
            is SearchParamsDomain.RentalItem.Accommodation -> searchParams.copy(
                accommodationTypes = if (chip.selected) {
                    searchParams.accommodationTypes.minus(chip.payload)
                } else {
                    searchParams.accommodationTypes.plus(chip.payload)
                }
            )
            else -> null
        }
    )

    fun onBedroomsChipClick(chip: ChipUIModel<String>) = updateSearchParams(
        when (val searchParams = savedSearchParamsDomain) {
            is SearchParamsDomain.RentalItem.Accommodation -> searchParams.copy(
                bedrooms = if (chip.selected) {
                    searchParams.bedrooms.minus(chip.payload)
                } else {
                    searchParams.bedrooms.plus(chip.payload)
                }
            )
            else -> null
        }
    )

    fun onAdditionsChipClick(chip: ChipUIModel<AccommodationAdditionDomain>) = updateSearchParams(
        when (val searchParams = savedSearchParamsDomain) {
            is SearchParamsDomain.RentalItem.Accommodation -> searchParams.copy(
                additions = if (chip.selected) {
                    searchParams.additions.minus(chip.payload)
                } else {
                    searchParams.additions.plus(chip.payload)
                }
            )
            else -> null
        }
    )

    private fun loadScreen() = launch {
        setState(SearchFilterUIState.Loading)
        getUserSettingsUseCase().fold(
            ifLeft = { setState(SearchFilterUIState.Error(it)) },
            ifRight = { userSettings = it }
        )
        getUserCountryUseCase().fold(
            ifLeft = { setState(SearchFilterUIState.Error(it)) },
            ifRight = { countryInfo = it }
        )
        updateSearchParams(params.searchParamsDomain)
    }



    private fun updateSearchParams(searchParamsDomain: SearchParamsDomain?) {
        val selectedCity = searchParamsDomain?.city
        val cities = countryInfo?.cities?.map { it.city }
            ?: throw IllegalStateException("Can't get cities")
        val districts = selectedCity?.let {
            countryInfo?.cities
                ?.first { it.city == selectedCity}
                ?.districts
        } ?: emptyList()
        val additions = countryInfo?.accommodationAdditions
            ?: throw IllegalStateException("Can't get additions")
        val accommodationTypes = countryInfo?.accommodationTypes
            ?: throw IllegalStateException("Can't get accommodation types")
        val currency = userSettings?.currency?.toUIModel()
            ?: throw IllegalStateException("Can't get currency")

        launch {
            when (searchParamsDomain) {
                is SearchParamsDomain.RentalItem.Accommodation -> {
                    setState(
                        SearchFilterUIState.Data.Accommodation(
                            selectedCity = selectedCity,
                            cities = cities,
                            selectedDistricts = searchParamsDomain.districts,
                            districts = districts.map {
                                ChipUIModel(
                                    text = it,
                                    selected = searchParamsDomain.districts.contains(it),
                                    payload = it,
                                )
                            },
                            minCost = searchParamsDomain.costMin,
                            maxCost = searchParamsDomain.costMax,
                            currency = currency,
                            additions = additions.map {
                                ChipUIModel(
                                    text = it.toUIModel().key,
                                    selected = searchParamsDomain.additions.contains(it),
                                    payload = it,
                                )
                            },
                            accommodationTypes = accommodationTypes.map {
                                ChipUIModel(
                                    text = it.toUIModel().value,
                                    selected = searchParamsDomain.accommodationTypes.contains(it),
                                    payload = it
                                )
                            },
                            bedrooms = bedrooms.map {
                                ChipUIModel(
                                    text = it,
                                    selected = searchParamsDomain.bedrooms.contains(it),
                                    payload = it,
                                )
                            }
                        )
                    )
                }
                is SearchParamsDomain.RentalItem.Transport -> {
                    setState(
                        SearchFilterUIState.Data.Transport(
                            selectedCity = selectedCity,
                            cities = cities,
                            districts = districts.map {
                                ChipUIModel(
                                    text = it,
                                    selected = searchParamsDomain.districts.contains(it),
                                    payload = it
                                )
                            },
                            selectedDistricts = searchParamsDomain.districts,
                            currency = currency,
                            minCost = searchParamsDomain.costMin,
                            maxCost = searchParamsDomain.costMax
                        )
                    )
                }
                else -> Unit
            }
        }

        savedSearchParamsDomain = searchParamsDomain
    }
}